import { useState } from "react";
import { toast } from "sonner";
import { Upload } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";

const ORIGINS = ["real", "test", "demo"];
const parseRows = (raw) => raw.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
  const [legal_name, industry, business_email, country] = l.split(",");
  return { legal_name: (legal_name || "").trim(), industry: (industry || "").trim(), business_email: (business_email || "").trim(), country: (country || "").trim() };
});

export const ImportCsvDialog = ({ open, onOpenChange, onImported }) => {
  const [raw, setRaw] = useState("");
  const [origin, setOrigin] = useState("real");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const reset = () => { setRaw(""); setOrigin("real"); setPreview(null); setError(""); };

  const run = async (dry) => {
    if (busy) return;
    const rows = parseRows(raw);
    if (!rows.length) { setError("Paste at least one CSV row (legal_name,industry,business_email,country)."); return; }
    setError(""); setBusy(true);
    try {
      const { data } = await api.post("/admin/leads/import/csv", { rows, dry_run: dry, data_origin: origin });
      if (dry) { setPreview(data); }
      else { toast.success(`Imported ${data.imported}, ${data.duplicates.length} duplicate, ${data.invalid.length} invalid.`); reset(); onOpenChange(false); onImported?.(); }
    } catch (e) { setError(formatApiError(e?.response?.data?.detail) || "Import failed."); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!busy) { onOpenChange(o); if (!o) reset(); } }}>
      <DialogContent className="max-w-xl" data-testid="import-csv-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Upload size={18} className="text-[#047857]" /> Import leads from CSV</DialogTitle>
          <DialogDescription>One lead per line as <code className="text-xs">legal_name,industry,business_email,country</code>. Run a dry-run preview first to see valid / duplicate / invalid counts.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 py-1">
          <Textarea data-testid="import-csv-textarea" value={raw} onChange={(e) => { setRaw(e.target.value); setPreview(null); }} rows={6} placeholder={"Meridian Cocoa Exporters,Agriculture,trade@meridian.example,Ghana\nNordic Fresh Imports,Food Distribution,buy@nordic.example,Sweden"} className="font-mono text-xs" />
          <div className="flex items-center gap-3">
            <Label className="text-xs">Data origin</Label>
            <Select value={origin} onValueChange={(v) => { setOrigin(v); setPreview(null); }}>
              <SelectTrigger data-testid="import-csv-origin" className="w-40 h-8"><SelectValue /></SelectTrigger>
              <SelectContent>{ORIGINS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
            </Select>
            {origin !== "real" && <span className="text-xs text-amber-600">Excluded from real beta metrics.</span>}
          </div>
          {preview && (
            <div className="rounded-md border border-slate-200 p-3 text-sm" data-testid="import-csv-preview">
              <div className="flex gap-4 text-xs mb-2">
                <span className="text-green-700">Valid: <b>{preview.valid}</b></span>
                <span className="text-amber-700">Duplicates: <b>{preview.duplicates.length}</b></span>
                <span className="text-red-700">Invalid: <b>{preview.invalid.length}</b></span>
              </div>
              {(preview.preview || []).slice(0, 5).map((r, i) => <div key={i} className="text-xs text-slate-500 truncate">{r.legal_name} · {r.industry || "—"} · {r.business_email || "—"}</div>)}
              {preview.invalid.slice(0, 3).map((r, i) => <div key={`iv${i}`} className="text-xs text-red-500">Row {r.row}: {r.error}</div>)}
            </div>
          )}
          {error && <p className="text-sm text-red-600" data-testid="import-csv-error">{error}</p>}
        </div>
        <DialogFooter>
          <button data-testid="import-csv-cancel" disabled={busy} onClick={() => onOpenChange(false)} className="h-9 px-3 rounded-md border border-slate-200 text-sm disabled:opacity-40">Cancel</button>
          <button data-testid="import-csv-dryrun" disabled={busy} onClick={() => run(true)} className="h-9 px-3 rounded-md border border-[#047857] text-[#047857] text-sm disabled:opacity-40">{busy ? "Checking…" : "Dry-run preview"}</button>
          <button data-testid="import-csv-submit" disabled={busy || !preview || preview.valid === 0} onClick={() => run(false)} className="h-9 px-4 rounded-md bg-[#047857] text-white text-sm disabled:opacity-40">Import {preview ? `${preview.valid}` : ""}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
